// src/live/components/LiveNow.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToPublicLives } from '../../lib/services/liveIndexService';
import { useCurrentUser } from '../../contexts/UserContext';
import { LiveIndexEntry } from '../types/liveIndex';
import { useMyUpcomingEvents } from '../hooks/useMyUpcomingEvents';
import LiveCard from './LiveCard';

const DISPLAY_LIMIT = 10;

export default function LiveNow() {
  const [publicLives, setPublicLives] = useState<LiveIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.uid;

  // Host's own events (appear immediately, regardless of status)
  const myEvents = useMyUpcomingEvents(userId);

  useEffect(() => {
    const unsubscribe = subscribeToPublicLives((data) => {
      setPublicLives(data);
      setLoading(false);
    }, userId);
    return () => unsubscribe();
  }, [userId]);

  // Merge: host's own events first, then public lives (deduped by id)
  const lives = React.useMemo(() => {
    const seen = new Set<string>();
    const merged: LiveIndexEntry[] = [];
    for (const ev of myEvents) {
      if (!seen.has(ev.id)) { seen.add(ev.id); merged.push(ev); }
    }
    for (const live of publicLives) {
      if (!seen.has(live.id)) { seen.add(live.id); merged.push(live); }
    }
    return merged.slice(0, DISPLAY_LIMIT);
  }, [myEvents, publicLives]);

  if (loading && lives.length === 0) {
    return (
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--vb-accent)' }}>
          Live Now
        </h2>
        <div style={{ color: 'var(--vb-text-secondary)' }}>Loading live streams...</div>
      </section>
    );
  }

  if (!lives.length) {
    return (
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--vb-accent)' }}>
          Live Now
        </h2>
        <div style={{ color: 'var(--vb-text-secondary)' }}>No live streams right now.</div>
      </section>
    );
  }

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold" style={{ color: 'var(--vb-accent)' }}>
          Live Now
        </h2>
        <button
          onClick={() => navigate('/live')}
          className="text-sm hover:underline"
          style={{ color: 'var(--vb-accent)' }}
        >
          See All
        </button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {lives.map((live) => (
          <LiveCard key={live.id} live={live} compact />
        ))}
      </div>
    </section>
  );
}
